import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { incrementActionCounter } from "../../utils/actionCounters";
import { fetchPolicies, fetchPoliciesForMultipleUsers } from "../policies/policySlice";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { FiSearch } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Pagination from "@mui/material/Pagination";
import Link from "@mui/material/Link";
import useMediaQuery from "@mui/material/useMediaQuery";
import MobileBalance from "./MobileBalance";
import AdminFinance from "./AdminFinance";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";
import { getPolicyCountsByCategory } from "../../utils/countUtils";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import RemindDialog from "../../components/common/RemindDialog";
import dayjs from "dayjs";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const getCalendarConfig = () => {
  return {
    calendarType: 'gregory',
    locale: 'en',
    maxDetail: 'month',
    minDetail: 'month'
  };
};

// Helper to convert values like "25,000", "₹25000", null -> number
const toNumber = (val) => {
  if (val == null) return 0;
  const cleaned = String(val).replace(/[^0-9.-]/g, "");
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
};

const BalanceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { policies, loading } = useSelector((state) => state.policies);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();

  // All hooks must be called before any conditional returns
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("motor");
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [selectedPolicyForRemind, setSelectedPolicyForRemind] = useState(null);
  const isTinyScreen = useMediaQuery('(max-height:720px)');  // e.g. 13" laptop
  const isShortScreen = useMediaQuery('(max-height:900px)'); // e.g. 14–15" laptop
  const isMediumScreen = useMediaQuery('(max-height:1080px)'); // full HD
  const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 8;
  const categories = ["motor", "non-motor", "life"];
  const isTablet = useMediaQuery('(max-width:1024px)');
  const isSmallScreen = useMediaQuery('(max-width:1281px)');

  // Filter state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sortDirection, setSortDirection] = useState(null); // null = default (latest created), 'asc' or 'desc' for balance
  const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null); // 'start' or 'end'
  const calendarRef = React.useRef();
  const isInitialMount = useRef(true); // Track initial mount to avoid saving empty state

  const handleFilterButtonClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const clearFilters = () => {
    setDateRange({ start: null, end: null });
    setSortDirection(null); // Reset to default (latest created)
  };

  const handleDateRangeCalendarChange = (date) => {
    const selectedDay = dayjs(date);
    if (showDateRangeCalendar === 'start') {
      setDateRange((prev) => ({
        start: selectedDay,
        end: prev.end && selectedDay.isAfter(prev.end) ? null : prev.end
      }));
      setShowDateRangeCalendar('end');
    } else if (showDateRangeCalendar === 'end') {
      // Validation: end date cannot be before start date
      if (selectedDay.isBefore(dateRange.start, 'day')) {
        return;
      }
      setDateRange((prev) => ({
        start: prev.start,
        end: selectedDay
      }));
      setShowDateRangeCalendar(null);
    }
  };

  const activeFilterCount = (dateRange.start || dateRange.end) ? 1 : 0;


  const isMobile = shouldUseMobileLayout();

  // Admin users see merged Finance screen
  if (isAdmin) {
    return <AdminFinance />;
  }


  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0) {
      console.log('Admin user - fetching aggregated policies for balance:', userIds);
      dispatch(fetchPoliciesForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      console.log('Staff user - fetching own policies for balance');
      dispatch(fetchPolicies(user.uid));
    }
  }, [user?.uid, dispatch, isAdmin, userIds, staffLoading]);

  // Restore page and category from navigation state
  useEffect(() => {
    if (location.state?.currentPage) {
      setCurrentPage(location.state.currentPage);
    }
    if (location.state?.fromCategory) {
      setSelectedCategory(location.state.fromCategory);
    }
  }, [location.state]);

  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('balanceList_filterState');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSortDirection(filters.sortDirection || null);
        // Restore date range
        if (filters.dateRange) {
          setDateRange({
            start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
            end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
          });
        }
      } catch (error) {
        console.error('Error restoring filter state:', error);
      }
    }
    // Delay setting isInitialMount to false to ensure all state updates complete
    setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
  }, []); // Run only on mount

  // Save filter state to sessionStorage whenever filters change (but not on initial mount)
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting with empty state
    if (isInitialMount.current) {
      return;
    }

    const filterState = {
      sortDirection,
      dateRange: {
        start: dateRange.start ? dateRange.start.toISOString() : null,
        end: dateRange.end ? dateRange.end.toISOString() : null
      }
    };
    sessionStorage.setItem('balanceList_filterState', JSON.stringify(filterState));
  }, [sortDirection, dateRange]);


  const filtered = policies.filter((p) => {
    // Exclude new leads
    if (p.status === "New Lead") return false;

    const categoryMatch =
      selectedCategory === "motor"
        ? p.policyCategory === "motor"
        : selectedCategory === "non-motor"
          ? p.policyCategory === "non-motor"
          : p.policyCategoryType === selectedCategory;

    if (!categoryMatch) return false;

    // Calculate balance for this policy
    const relevantAmount = toNumber(p.amountPayable) || (toNumber(p.premiumAmount || p.totalPremium) - toNumber(p.discount));
    const paid = toNumber(p.paidAmount ?? p.amountPaid ?? p.amountReceived);
    const balance = relevantAmount - paid;

    // Skip if balance is 0 or negative
    if (balance <= 0) return false;

    const searchLower = searchTerm.toLowerCase();

    // Base search keys
    const hitsBase =
      (p.name || "").toLowerCase().includes(searchLower) ||
      (p.mobile || "").includes(searchTerm);

    // Date Range Filter (createdAt)
    if (dateRange.start && dateRange.end) {
      const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
      if (!created || !created.isValid()) return false;

      const start = dateRange.start.startOf('day');
      const end = dateRange.end.endOf('day');

      if (created.isBefore(start) || created.isAfter(end)) return false;
    }

    if (selectedCategory === "motor") {
      // Add vehicle number search for motor
      const searchClean = searchLower.replace(/-/g, "");
      const vehicleNumberClean = (p.vehicleNumber || "")
        .toLowerCase()
        .replace(/-/g, "");
      const hitVehicle = vehicleNumberClean.includes(searchClean);
      return hitsBase || hitVehicle;
    }

    return hitsBase;
  });

  // Sort by Balance or default (latest created)
  const sorted = filtered.sort((a, b) => {
    if (sortDirection === null) {
      // Default: Sort by createdAt (newest first)
      const dateA = a.createdAt ? (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : new Date(0);
      const dateB = b.createdAt ? (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : new Date(0);
      return dateB - dateA;
    } else {
      // Sort by Balance
      const amountA = toNumber(a.amountPayable) || (toNumber(a.premiumAmount || a.totalPremium) - toNumber(a.discount));
      const paidA = toNumber(a.paidAmount ?? a.amountPaid ?? a.amountReceived);
      const balanceA = amountA - paidA;

      const amountB = toNumber(b.amountPayable) || (toNumber(b.premiumAmount || b.totalPremium) - toNumber(b.discount));
      const paidB = toNumber(b.paidAmount ?? b.amountPaid ?? b.amountReceived);
      const balanceB = amountB - paidB;

      return sortDirection === 'asc'
        ? balanceA - balanceB
        : balanceB - balanceA;
    }
  });


  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(sorted.length / itemsPerPage);

  // Calculate counts for each category with cross-tab filtering
  // Date filter applies to all tabs
  const categoryCounts = useMemo(() => {
    const counts = { motor: 0, 'non-motor': 0, life: 0 };

    policies.forEach((p) => {
      if (p.status === "New Lead") return;

      // Calculate balance
      const relevantAmount = toNumber(p.amountPayable) || (toNumber(p.premiumAmount || p.totalPremium) - toNumber(p.discount));
      const paid = toNumber(p.paidAmount ?? p.amountPaid ?? p.amountReceived);
      const balance = relevantAmount - paid;

      // Skip if balance is 0 or negative
      if (balance <= 0) return;

      // Apply date range filter (cross-tab)
      if (dateRange.start && dateRange.end) {
        const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
        if (!created || !created.isValid()) return;

        const start = dateRange.start.startOf('day');
        const end = dateRange.end.endOf('day');

        if (created.isBefore(start) || created.isAfter(end)) return;
      }

      // Determine category
      if (p.policyCategory === 'motor') {
        counts.motor++;
      } else if (p.policyCategory === 'non-motor') {
        counts['non-motor']++;
      } else if (p.policyCategoryType === 'life') {
        counts.life++;
      }
    });

    return counts;
  }, [policies, dateRange]);

  const rows = paginated.map((p, index) => {
    const relevantAmount = toNumber(p.amountPayable) || (toNumber(p.premiumAmount || p.totalPremium) - toNumber(p.discount));
    const paid = toNumber(p.paidAmount ?? p.amountPaid ?? p.amountReceived);
    const balance = relevantAmount - paid;

    return {
      id: p.id || index,
      name: p.name,
      mobile: p.mobile,
      policyCategoryType: p.policyCategoryType,
      vehicleNumber: p.vehicleNumber,
      vehicleMake: p.vehicleMake,
      premiumAmount: toNumber(p.premiumAmount || p.totalPremium || 0),
      discount: toNumber(p.amountPayable) || (toNumber(p.premiumAmount || p.totalPremium) - toNumber(p.discount)), // Kept key 'discount' to minimize render changes, but value is amountPayable
      balance: balance,
      original: p
    };
  });

  // Format number with thousands separator
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  };

  // Generate WhatsApp message for balance reminders
  const generateBalanceMessage = async (policy) => {
    const relevantAmount = toNumber(policy.amountPayable) || (toNumber(policy.premiumAmount || policy.totalPremium) - toNumber(policy.discount));
    const paid = toNumber(policy.paidAmount ?? policy.amountPaid ?? policy.amountReceived);
    const balanceAmount = relevantAmount - paid;

    if (balanceAmount <= 0) return "";

    const policyType = policy.policyType || "Policy";
    const companyName = policy.policyCompany || "your insurer";
    const isMotor = policy.policyCategory === "motor";
    const vehicleMake = policy.vehicleMake || "";
    const vehicleModel = policy.vehicleModel || "";
    const vehicleNumber = policy.vehicleNumber || "";
    const clientName = policy.name || "";
    const greeting = clientName ? `Hi ${clientName}` : "Hi";

    let message = "";

    // Get agent details from Firestore with fallbacks
    let agentName = 'your agent';
    let agentMobile = '';

    try {
      if (user?.uid) {
        const agentDoc = await getDoc(doc(db, 'agent-details', user.uid));
        if (agentDoc.exists()) {
          const agentData = agentDoc.data();
          agentName = agentData.name || agentData.displayName || 'your agent';
          agentMobile = agentData.phone || agentData.phoneNumber || '';
        }
      } else {
        // Fallback to policy data if user is not available
        agentName = policy.agentName || 'your agent';
        agentMobile = policy.agentMobile || '';
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      // Fallback to policy data if there's an error
      agentName = policy.agentName || 'your agent';
      agentMobile = policy.agentMobile || '';
    }

    if (isMotor && vehicleModel && vehicleNumber) {
      message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType} policy* ,${vehicleMake} ${vehicleModel} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    } else if (isMotor && vehicleNumber) {
      message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType} policy* ,${vehicleMake} - *${vehicleNumber}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    } else {
      message = `${greeting}\nYour pending balance of *₹${formatAmount(balanceAmount)}* for your *${policyType}* with ${companyName} is still due❗\n\nPlease pay at the earliest, for more information contact your agent: ${agentName} at ${agentMobile}`;
    }

    return message;
  };

  // Handle WhatsApp redirect (reuse SuccessDialog's logic)
  const handleWhatsAppRedirect = (message) => {
    if (!message) {
      console.error("No message provided for WhatsApp");
      return;
    }

    const whatsappAppUrl = `whatsapp://send?text=${encodeURIComponent(message)}`;
    const webWhatsappUrl = `https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`;

    // Try app first using a hidden iframe to avoid console errors
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = whatsappAppUrl;
    document.body.appendChild(iframe);

    // Remove iframe after a short delay
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 100);

    // Listen for page blur (indicates app opened)
    let appOpened = false;
    const handleBlur = () => {
      appOpened = true;
      window.removeEventListener('blur', handleBlur);
    };
    window.addEventListener('blur', handleBlur);

    // Fallback to web after 1.5 seconds ONLY if app didn't open
    setTimeout(() => {
      window.removeEventListener('blur', handleBlur);
      if (!appOpened) {
        window.open(webWhatsappUrl, "_blank", "noopener,noreferrer");
      }
    }, 1500);
  };

  const handleRemindClick = (policy, event) => {
    event.stopPropagation(); // Prevent row click navigation
    setSelectedPolicyForRemind(policy);
    setRemindDialogOpen(true);
  };

  const handleRemindConfirm = async () => {
    if (selectedPolicyForRemind) {
      try {
        const message = await generateBalanceMessage(selectedPolicyForRemind);
        handleWhatsAppRedirect(message);

        if (user?.uid) {
          await incrementActionCounter(user.uid, 'balanceremindercounter');
        }
      } catch (error) {
        console.error('Error generating balance message:', error);
        // Fallback to a default message if there's an error
        handleWhatsAppRedirect("Your balance reminder couldn't be generated. Please try again or contact support.");
      }
    }
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  const handleRemindClose = () => {
    setRemindDialogOpen(false);
    setSelectedPolicyForRemind(null);
  };

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'Roboto Slab',
          fontWeight: 550,
          fontSize: isSmallScreen ? '24px' : '32px',
          fontStyle: 'Medium',
          lineHeight: isSmallScreen ? '40px' : '56px',
          letterSpacing: '0.02em',
          opacity: 1,
          height: isSmallScreen ? 40 : 56,
          textEmphasisColor: '#212121',
          mb: 1
        }}
      >
        Finance
      </Typography>

      <Box
        sx={{
          width: '100%',
          background: '#fff',
          borderRadius: '0',
          boxShadow: 'none',
          px: isMobile ? 0.5 : { xs: 2, sm: 3 },
          pb: isMobile ? 1 : 3,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          minHeight: 'calc(100vh - 120px)', // Ensure container takes full height
          height: 'calc(100vh - 120px)', // Fixed height to prevent shrinking
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, newVal) => {
              setSelectedCategory(newVal);
              setCurrentPage(1);
            }}
            variant="standard"
            size="small"
            indicatorColor="primary"
            sx={{
              mb: 1,
              width: 'fit-content',
              justifyContent: 'flex-start',
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 500,
                minWidth: isSmallScreen ? 60 : 80,
                px: isSmallScreen ? 1.5 : 2,
                py: 0.5,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }
            }}
          >
            {categories.map((cat) => (
              <Tab
                key={cat}
                label={`${cat.charAt(0).toUpperCase() + cat.slice(1)} (${categoryCounts[cat] || 0})`}
                value={cat}
                sx={{
                  '&:hover': {
                    backgroundColor: '#E0EBFF',
                  },
                }}
              />
            ))}
          </Tabs>

          <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0} sx={{ mb: 1, mr: 1 }}>
            <Button
              variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
              startIcon={<FilterAltOutlinedIcon />}
              onClick={handleFilterButtonClick}
              sx={{
                borderRadius: '8px',
                px: isSmallScreen ? 1.5 : 2.5,
                height: isSmallScreen ? 32 : 40,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                backgroundColor: activeFilterCount > 0 ? '#0063F7' : '#fff',
                color: activeFilterCount > 0 ? '#fff' : '#1F2937',
                borderColor: '#E0E0E0',
                '&:hover': {
                  color: activeFilterCount > 0 ? '#fff' : '#004DC2',
                  backgroundColor: activeFilterCount > 0 ? '#0056d6' : '#F5F8FF'
                }
              }}
            >
              Filter
            </Button>
          </Badge>
        </Box>
        <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />


        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search client by name or phone number"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                <FiSearch />
              </Box>
            )
          }}
          sx={{ mb: 0.5 }}
        />

        {loading ? (
          <Typography>Loading policies...</Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0,
                px: 0,
                mx: isMobile ? -0.5 : { xs: -2, sm: -3 }, // Extend table beyond container padding
                width: isMobile ? 'calc(100% + 8px)' : { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' }, // Compensate for negative margin
                overflow: 'visible', // Allow natural content flow without internal scrolling
                // Remove flex properties to prevent row stretching
                '& .MuiTableHead-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTable-root': {
                  borderLeft: 'none',
                  borderRight: 'none',
                },
                '& .MuiTableRow-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiTableCell-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiPaper-root': {
                  borderRadius: '0 !important',
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                  borderBottomLeftRadius: '0 !important',
                  borderBottomRightRadius: '0 !important',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                  boxShadow: 'none',
                  borderBottom: '1px solid #E0E0E0',
                },
              }}
            >
              <Table
                size="medium"
                aria-label="balance table"
                sx={{
                  border: 'none',
                  borderCollapse: 'collapse',
                  tableLayout: isMobile ? 'auto' : 'fixed',
                  width: '100%',
                  // Remove flexGrow to prevent row stretching
                  '& th, & td': {
                    border: 'none',
                    px: 0,
                  },
                  '& td, & th': {
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                  '& td:first-of-type, & th:first-of-type': {
                    pl: 4,
                  },
                  '& tr': {
                    borderBottom: '1px solid #E0E0E0',
                  },
                  '& tr:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 60 : '10%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 60, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>

                        <span style={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          width: 40,
                          height: 24,
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>#</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 120 : '25%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Customer
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 100 : '20%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        {selectedCategory === "motor" ? (
                          <>
                            <DirectionsCarIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: isSmallScreen ? '12px' : '16px',
                              lineHeight: isSmallScreen ? '18px' : '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Vehicle
                            </Typography>
                          </>
                        ) : (
                          <>
                            <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: isSmallScreen ? '12px' : '16px',
                              lineHeight: isSmallScreen ? '18px' : '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Category
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '18%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <MonetizationOnIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          {isSmallScreen ? 'Premium' : 'Total Premium'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '22%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <MonetizationOnIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          {isSmallScreen ? 'Payable' : 'Amount Payable'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '15%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <AccountBalanceWalletIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Balance
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, pl: isSmallScreen ? 1 : 0, width: isMobile ? 120 : '20%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <span style={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          height: 24,
                          opacity: 1,
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}><MoreVertIcon sx={{ width: isSmallScreen ? 15 : 24, height: isSmallScreen ? 15 : 24, color: '#4B5563DE' }} /> Actions</span>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow sx={{ height: 48, verticalAlign: 'middle' }}>
                      <TableCell colSpan={7} align="center" sx={{ px: 0 }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#6B7280',
                            mb: 1
                          }}>
                            No records found. Great time to follow up with new leads!
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/policy/${row.id}`, { state: { policy: row.original, fromScreen: 'balance', fromPage: currentPage, fromCategory: selectedCategory } })}
                        sx={{
                          height: 48,
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#E0EBFF !important' }
                        }}
                      >
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 60 : '10%',
                            maxWidth: isMobile ? 60 : '10%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div style={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#212121',
                            height: '48px',
                            width: '100%',
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '25%',
                            maxWidth: isMobile ? 120 : '25%',
                            verticalAlign: 'middle',
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                              maxWidth: '100%',
                              display: 'block',
                            }}
                          >
                            {row.name || "-"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '24px',
                              letterSpacing: '0.01em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {row.mobile || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 100 : '20%',
                            maxWidth: isMobile ? 100 : '20%',
                            verticalAlign: 'middle',
                          }}
                        >
                          {selectedCategory === "motor" ? (
                            <>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto',
                                  fontWeight: 400,
                                  fontSize: '16px',
                                  lineHeight: '24px',
                                  letterSpacing: '0.02em',
                                  color: '#212121',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  opacity: 1,
                                  width: '100%',
                                }}
                              >
                                {row.vehicleMake || "-"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '24px',
                                  letterSpacing: '0.01em',
                                  color: '#212121',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  width: '100%',
                                }}
                              >
                                {row.vehicleNumber || "-"}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                letterSpacing: '0.02em',
                                color: '#212121',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: 1,
                                width: '100%',
                              }}
                            >
                              {row.policyCategoryType || "-"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '18%',
                            maxWidth: isMobile ? 'none' : '18%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            ₹ {formatAmount(row.premiumAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '22%',
                            maxWidth: isMobile ? 'none' : '22%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#166434',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            ₹ {formatAmount(row.discount || row.premiumAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            pr: isSmallScreen ? 1 : 0,
                            width: isMobile ? 120 : '15%',
                            maxWidth: isMobile ? 'none' : '15%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: row.balance > 0 ? '#991B1B' : '#166434',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            ₹ {formatAmount(row.balance)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            pl: isSmallScreen ? 1 : 0,
                            width: isMobile ? 120 : '20%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div
                            onClick={(e) => handleRemindClick(row.original, e)}
                            role="button"
                            tabIndex={0}
                            aria-label="Send Remainder"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRemindClick(row.original, e);
                              }
                            }}
                            style={{
                              height: '28px',
                              minWidth: isSmallScreen ? '90px' : '130px',
                              maxWidth: isSmallScreen ? '90px' : '130px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              borderRadius: '12px',
                              padding: isSmallScreen ? '4px 8px' : '4px 12px',
                              backgroundColor: 'transparent',
                              border: '1px solid #166434',
                              outline: 'none',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              flexShrink: 0,
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#E6F9EB';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <NotificationsIcon sx={{ width: 16, height: 16, color: '#166434' }} />
                            <span
                              style={{
                                color: '#166434',
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '0.01em',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {isSmallScreen ? 'Remind' : 'Reminder'}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}


                </TableBody>
              </Table>
            </TableContainer>

            {/* Filter Popover */}
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { p: 2.5, width: 380, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' } } }}
            >
              <Stack spacing={2}>
                {/* Sort By Balance */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                    Sort by Balance
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={sortDirection}
                      onChange={(e) => setSortDirection(e.target.value)}
                    >
                      <FormControlLabel
                        value="asc"
                        control={
                          <Radio
                            size="small"
                            onClick={(e) => {
                              if (sortDirection === 'asc') {
                                e.preventDefault();
                                setSortDirection(null);
                              }
                            }}
                          />
                        }
                        label="Low to High"
                      />
                      <FormControlLabel
                        value="desc"
                        control={
                          <Radio
                            size="small"
                            onClick={(e) => {
                              if (sortDirection === 'desc') {
                                e.preventDefault();
                                setSortDirection(null);
                              }
                            }}
                          />
                        }
                        label="High to Low"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Divider />

                {/* Date Range Filter */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                    Date Range (Created)
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Button
                      variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                      sx={{
                        flex: 1,
                        '&:hover': {
                          backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                          borderColor: '#E0EBFF'
                        }
                      }}
                    >
                      {dateRange.start ? dateRange.start.format('DD MMM') : 'Start Date'}
                    </Button>
                    <Button
                      variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end')}
                      sx={{
                        flex: 1,
                        '&:hover': {
                          backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                          borderColor: '#E0EBFF'
                        }
                      }}
                      disabled={!Boolean(dateRange.start)}
                    >
                      {dateRange.end ? dateRange.end.format('DD MMM') : 'End Date'}
                    </Button>
                  </Stack>

                  {showDateRangeCalendar && (
                    <Box
                      ref={calendarRef}
                      sx={{
                        position: 'relative',
                        zIndex: 1300,
                        boxShadow: 3,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: '100%',
                        overflow: 'hidden',
                        padding: '12px',
                        mb: 1.5,
                        minHeight: '340px',
                        maxHeight: '340px',
                        '& .react-calendar': {
                          width: '100%',
                          maxWidth: '100%',
                          minHeight: '280px',
                          '&__navigation': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 6px',
                            margin: '0 0 8px 0',
                            '&__label': {
                              minWidth: '180px',
                              textAlign: 'center',
                              flexGrow: 0,
                              fontWeight: '600 !important',
                              margin: 0,
                              padding: '8px 0',
                              fontSize: '16px',
                              lineHeight: '1.4'
                            },
                            '&__arrow': {
                              minWidth: '32px',
                              padding: '8px 2px',
                              margin: 0
                            }
                          },
                          '&__month-view__weekdays': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0 6px',
                            marginBottom: '4px',
                            '&__weekday': {
                              width: '36px',
                              padding: '4px 0',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: '600 !important',
                              textTransform: 'uppercase',
                              color: '#666 !important',
                              backgroundColor: 'transparent !important',
                              border: 'none !important',
                              '& abbr': {
                                textDecoration: 'none',
                                display: 'inline-block',
                                width: '100%',
                                textAlign: 'center'
                              }
                            }
                          },
                          '&__month-view__days': {
                            display: 'grid !important',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '4px',
                            padding: '0 6px',
                            '&__day': {
                              width: '36px',
                              height: '36px',
                              margin: '0 auto',
                              fontWeight: '600 !important',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              '&--neighboringMonth': {
                                opacity: 0.5
                              },
                              '&--weekend': {
                                color: '#212121 !important'
                              },
                              '&--now': {
                                fontWeight: 'bold',
                                color: '#004DC2 !important',
                                backgroundColor: 'transparent !important',
                                '&:enabled:hover': {
                                  backgroundColor: '#E0EBFF !important'
                                }
                              }
                            }
                          }
                        },
                        '& .react-calendar__tile': {
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '13px',
                          color: '#212121 !important',
                          transition: 'all 0.2s ease',
                          '&:enabled:hover': {
                            backgroundColor: '#E0EBFF !important',
                            color: '#212121 !important'
                          },
                          '&--active, &--active:enabled, &--active:enabled:hover': {
                            backgroundColor: '#004DC2 !important',
                            color: '#fff !important',
                            borderRadius: '50%',
                            border: 'none',
                            boxShadow: 'none',
                            outline: 'none'
                          }
                        },
                        '& .react-calendar__tile--active, & .react-calendar__tile--active:enabled': {
                          backgroundColor: '#004DC2 !important',
                          color: '#fff !important'
                        },
                        '& .react-calendar__month-view__days__day--weekend': {
                          color: '#212121 !important'
                        },
                        '& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover': {
                          backgroundColor: '#E0EBFF !important'
                        },
                        '& .react-calendar__navigation button': {
                          color: '#333 !important',
                          background: 'none !important',
                          fontWeight: '600 !important',
                          border: 'none !important',
                          outline: 'none !important',
                          '&:hover': {
                            backgroundColor: '#E0EBFF !important',
                            color: '#333 !important'
                          },
                          '&:focus': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important',
                            boxShadow: 'none !important'
                          },
                          '&:active': {
                            backgroundColor: 'transparent !important'
                          }
                        },
                        '& .react-calendar__navigation__label': {
                          color: '#333 !important',
                          background: 'transparent !important',
                          fontWeight: '600 !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&:focus': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important'
                          }
                        }
                      }}
                    >
                      <Calendar
                        value={showDateRangeCalendar === 'start' ? dateRange.start?.toDate() : dateRange.end?.toDate()}
                        onChange={handleDateRangeCalendarChange}
                        {...getCalendarConfig()}
                      />
                    </Box>
                  )}
                </Box>

                <Divider />

                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button
                    onClick={clearFilters}
                    size="small"
                    color="inherit"
                    sx={{
                      '&:hover': {
                        backgroundColor: '#F5F5F5'
                      }
                    }}
                  >
                    Clear filters
                  </Button>
                  <Button onClick={handleFilterClose} size="small" variant="contained">
                    Done
                  </Button>
                </Stack>
              </Stack>
            </Popover>

            {/* Spacer to push pagination to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Pagination moved below */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 'auto', // Push pagination to bottom
                  backgroundColor: '#fff',
                  borderRadius: '0',
                  boxShadow: 'none',
                  pt: 1,
                  pb: 1,
                }}
              >
                <Pagination
                  count={Math.max(totalPages, 1)}
                  page={currentPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '50%',
                      border: '1px solid #D1D5DB',
                      width: 36,
                      height: 36,
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                      },
                    },
                    '& .MuiPaginationItem-previousNext': {
                      borderRadius: 0,
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: 'auto',
                      height: 'auto',
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#004DC2 !important',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      <RemindDialog
        isOpen={remindDialogOpen}
        onClose={handleRemindClose}
        onConfirm={handleRemindConfirm}
        message="Send balance reminder via WhatsApp?"
      />
    </Box >
  );
};

export default BalanceList;
